import { createMachine, assign, fromPromise } from "xstate";
import { toast } from "sonner";
import { pdf2pngs } from "@/lib/utils";
import {
  Canvas,
  Circle,
  Rect,
  IText,
  Group,
  FabricObject,
  Point,
  TPointerEvent,
  FabricImage,
} from "fabric";
import { typstLayoutTemplate } from "./typst";
import { Configuration } from "@/api/get-configuration";
import { PageSizes } from "@cantoo/pdf-lib";

// Helper function to convert hex to rgba with opacity
const hexToRgba = (hex: string, opacity: number = 1) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Define the context type for our state machine
export interface ImageEditorContext {
  canvasApis: Canvas[];
  current: number;
  circleCounter: number | string;
  squareCounter: number | string;
  selectedColor: string;
  showNumbers: boolean;
  shapeSize: "s" | "m" | "l";
  zoomLevel: number;
  files: File[];
  configuration?: Configuration;
  logo?: File;
  onSave?: (canvases: Canvas[]) => Promise<void>;
  error?: string;
}

// Define the events that can be sent to the state machine
export type ImageEditorEvent =
  | { type: "LOAD_FILES"; files: File[] }
  | { type: "TOGGLE_CIRCLE_MODE" }
  | { type: "TOGGLE_SQUARE_MODE" }
  | { type: "TOGGLE_PAINT_MODE" }
  | { type: "TOGGLE_PAN_MODE" }
  | { type: "TOGGLE_NUMBERS" }
  | { type: "SET_COLOR"; color: string }
  | { type: "SET_SHAPE_SIZE"; size: "s" | "m" | "l" }
  | { type: "SET_CIRCLE_COUNTER"; counter: number | string }
  | { type: "SET_SQUARE_COUNTER"; counter: number | string }
  | { type: "INCREMENT_CIRCLE_COUNTER" }
  | { type: "INCREMENT_SQUARE_COUNTER" }
  | { type: "GO_TO_SLIDE"; index: number }
  | { type: "ZOOM_IN" }
  | { type: "ZOOM_OUT" }
  | { type: "RESET_ZOOM" }
  | { type: "REMOVE_IMAGE"; index: number }
  | { type: "REMOVE_ALL_IMAGES" }
  | { type: "EXPORT_PDF" }
  | { type: "ESCAPE_ALL_MODES" }
  | { type: "CANVAS_CLICK"; canvas: Canvas; pointer: { x: number; y: number }; target?: FabricObject }
  | { type: "FILES_LOADED"; canvases: HTMLCanvasElement[]; canvasApis: Canvas[] }
  | { type: "FILES_LOAD_ERROR"; error: string }
  | { type: "CANVASES_INITIALIZED"; canvasApis: Canvas[] }
  | { type: "EXPORT_COMPLETE" }
  | { type: "EXPORT_ERROR"; error: string };

export const imageEditorMachine = createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QEsC2BDGBRCyAuA9gE4B0yEANmAMQAyA8gIIAiA+gGICStWAygNoAGALqJQABwKx8yAgDsxIAB6IAjIMEA2EgGZVAJgCsATkEAWQQA5NhzToA0IAJ6IdAdn36SpwTs2C3M3dBVU0AXzDHNEwwHHxiMkoaAGFGADkANUZePlZONM4AFU5GWk4ALSxmIVEkEElpPFkFOpUEDzcSQ2sQz1VLVTNAxxcEHS1tcd87VRNLEIiojGxcQlJyKmpC+gBxHZ5WZM4AJWSDgFl6ZiwaxQaZeUU2syMu2c1LccNDN0NVYxGiH0xmMqhIliM4zcA00ZkMOkMixA0RW8XWSS2u32WFYvAAigBVRjHHGXa63Or3JqPVpqDSTAwmcxWGx2QEIIwhEgvQz6CxBfQ6SyWJEo2KrBIbGjbPYHAAKjHyhVYZJuIjuUgeLVAz3c3I+lhelg8ekM7M+ZhINhCWk8bg8lmMouW4rRiU2MuxrAVaRVVzVtQkmup2uUdK0ukZpgs1lsDmcrj5gnBmn0hrtsLMZmdMTia3d0qxBzSBPOACEsMcBOrKcHmk9EIEvN1-DodCDfCDNOzgW5k4LoWZjBDNNCc6j81LqDllcl6AxjhSg4167T2n3dKO06FVNDfG4e8bLbv4X4h+4TOPXZOMTPcQAJRhynG8CoBjUrmk6xuGZNuGxuAYw5-GYlg9valgkKoBgQno-huEKV55pKt5YLOJxnDic4EmkhSVku9R1l+YbtGmJDbm2gg-MY+h9mBCYch8XhUb4gjDrRgFuEhEropsd74kSJKHPQOF4YuNbLlqDakcebFmJomgdgpwI9t0hjeMabjGLCxjydpOjcW6U47PQrDbLiZTkhJhGfqGbS-MYUGAcYgFfKEII9vJOibrRw66WOkTIi6yG8TQ5T0PQ5x5GkBFUqu37tB84J-DYfL2oMCI9n4nRTGmtiDBBXGBWKIUFtQ4WRawImFLFRF2Y2sJOS5Hh2KOQ7dgxdpggO9qeAMvwisVwU8WVJJ3hV5y1bZ0kDU10IeO5sw9ieTm8t0unGu4hk3psJKXBkOKcOcjA7O+tbTWuvzJru-zdHy-T9B1oz6AEjnQbRcKaLMgGwttKG7Vg+04qUtB5Mdp3VoGNlSZdehQcY8LsVpfieOy-yfCQLGCn1+iKfCf2hdQWAABpyvQxzKnKzDsFNMMJeoEZ6EY0YsnGaM6C8ujuP4rGmH2RVLLmI1TnwqTPqwIN+tckMfnTJEIZaxgXtCJhfYET1qErza-oKli-kM3QEyQFAEOguByFA1AQPIYBkHIABuBAANa2yVI0m2byAWwgXuOwAxugIY1LTIbSQzDLM8ysZsgxQr9N4qZHgEW5GEbHvm5bYBEEQCTiBQgcAGbEKgZDDW66de1APsOwQAdByIIfxSRDOdFpfYIqB2lwvo7J+L45G-Nl3yMoY2ZDULRlyDI6AUMgABelfJOgDvoLAcBWzbduOy7pcT5OU9NDP8+L8v9ur3A1f+4HzTB9ZcXEW0eidAEAw-DMwoK73VidB-ELQUOQwghGy9tPWeC8LZLxXmvWA1As451IHnQuxdd4TklAfZAR9wFQEgWfaBl9a7X3kLfKG996pjF3JjaE3936bTML3foyYEa+HGDBcwQwjZ+2QEQP2VBzgEAgIWWUWEMIXH9I3B+rgKEv2oXoD+HNDymHBJCF6uMqJaQ4VwnhYA+ECOoKkTI2RDhlGSAAaXEWQ40XNVYWD5KBKwdDOpaW0O9e0v5ZiClUBo7hvD+E0FFk+YGtBQaqhludOWj8pFULfrI2hPZdJgh0JCQYvg9ahC8VonRNA7xzgXOY6SIIrQIzbLuDm38ELgQ0JjHWX0mL6HUIiceqDSCcO8do3x040IPgCbiN8eS1zzExkrf8egQgCi0D2VsGl7SgQ5qCR06SfG6OySIrCIlcL4TvnVaSPwrRsSFFoBG3whxmk6r+ToNFeQIhCDUiECy2m6M9MWUsFYqx9ISoBbwyTDTqAUp8cpnUzDQRTLROpHw9CALuZk6gJkzKmV4JZM6klQ79Mgr+DQdSXiDFHoIHunU0yOR6vdFGfxIXtImtFN5JFhxzRxSYDmtFbDLSVqtOpVhfCClJbo8l1VKVtGZWciEDLxhtkAj2NinR3qKUCH2F6NFOU0DGp0iavLECWIFe4NsegOwnOehoMELF+iAvcP0Q08rqB7XoAdMGJ1EXQ2Re8xyaKNCfG6OoWiaNoxTNHGywFo4GmCyaSQFpGT2kWqtZLI6NrQlIqbm0fwXRBRZk+AjWEicHGjAYZBC530hj-wUmakmZMKbempiq8hz8onDJNZ-Bi6g4aAVUYBUIqZ-VBT3gkWAABHAArugIg9zBFegEsSUkYjNkXXpj87kngUl83mBrDkJhOigVcdzbFSsjZdt7f2qF+isi8CMZwUxZbkmdD+D8T49TYkAt-APXkBVMV8gMo068Hae19oHUTXgYtAnBP9NGu1sa1CqESSQJxIIU3mFTLi56cJGFGD5ApUecJPEvtKluj9ULsnznJiezFmN2weLcX8FyWVR7kQMH2ZJoJfCbvfTu9p-FHzi1fJUPDXIgj-kTgaWEMGgRGEgrMEIwpk5WH0HR7dn7+KEhHcJUSGySFbLXCBwIYGBi+qPO4AEnVQjMWNG1QI4wvoScw+0x5OISzlkrAB0hYd2zeSMFpT4R5oLdDFQpTGRgdyAo0BYZ9AbX2kAwwx3RMLzLws4FZRTE7m4WEcoaQj0FhTCgXf8II3I9V2GsOCseAX0P0c-eS-IeH-gJr7oKfwesEZo0NDlNdVEWEcrQyNYLhWIpRR5eO8JahAWWlSR4BDUGfho1+N1UbxqXJutQ3llrBWoWKuVMqrr9rYtWGSiUwC9I9BoxsOpOpuNdawisCBkzIWFWA0tYdcGtrbPKYsMmBLpgBhGHhEMNG3w3puPqSM-wuW22Bta-Ni74agnWohieuOKYFYwihJ8NGXlMbGiXejTsra3ZukB+0wt5NKaluW0BhAunvIvBej8dsSt-jxmet0OrriNA4pehzI24h0BezwFC8z3pFS4SljdpTCUn6UNflWuR6bEBKz-AYL6kHAjDmZ6zuQ7P2l7sMWcI9Zj8cSIQJYts1icVJvseyPcXR6fzHhOrXk8u2dQv8eLSWISy2C+kdE6t8iGLDm6lLkDbZrCbSt4rrDnScm4c12QgpeNikKzKVTxAHxLScdsB4NhWZ-P-cCyQFn1v2lhbhQistwoTfosGBi7FfGED5XBIyRSAFR6DRm26TPAeyXtYpaH-Jgm260qVi8X4C75LqSulROwfJeSAX90rrlLfOvRe6wgflL87TwmYaK2OikrRI5eCCITqZU-o-zI3if53xrtfz2el+GrinavAt5FtOLtLdFAkOcfQOgZg75zF+yjr6dWARAMHFB5a17QugW1QhElR5MxwhmsG8FdD9zVgdf038bN+cSJ41Lkk12xbB5I0wxdtcwQnU78aIU9oJn8sdSYccS0aY281wndK0aEa1RhBwoIvtbEXJQRd8y599l4OciwcQfRecT1kkCNi9KdEZ3VY42xtALkERbA5IbB5c5Abdv1ul7d-0BDQNwMQQZCsCy8tVkwUZDRgItA0cODc4uDs9TJws88qD6ZycuY6ltIt8tAY9yE7AmDmEBxBRzATD20EFzDJ9KpisbDVtHIQh+gqE7BpgdVXAtBn5jQBhTBCNPh5CoVuUCQapgi2hklLQ2wINPhPBeMADnpJtPMl8fhbQd8UjQ0+AlUT9Mi1B2xIJSlehTA0wrBDxVNtJfIBxElesqjdEw0rsbUIdFE2w6lk0OJoIF0OIs10UtJmQZ0fDA0WcFDqjX8I1rskCP8GiPlpCd978XsXDcYIRwR3B-xyjnIXJ+i-EyDi0qZKCZ8Vssid9p1ScEIQQtUXCxjtBV0-hDQW1tIjYwAlBJAiAmgLYN45BXYa4d498EhgTQTwSq5fYCF65hAT0XiSc1FydPje55JuosxoIjN-g+w-s4TSAETiAkTYFs5c5848Ai4iAS5ySSBKSwTK58E64b4G56jCdUxFZjD9d5hxg4da13F9RVF7sPstokQ5BfF4A6hyTZYnjEAABaVQdkVU9SCDEEf8W6VKOpYBJIZUgnd4roPSX8O6CEY0ehUcHyfTIcNMOpAWNPUqCuC2E0rXFTZMCwI7Twb4P4OpXuLSS0aXb7Wwa04BdBTBE+KBOAT0shdQUMpHYEdsICX3ehBGTGGCd6ECAYdg3woNTRRZMABM6SN7TqR0CVX4NMC0CES5U7AdMs5TPwJooIR0FyNqRJVSP4LmXqeYUbLMF0lkg-TJZshKCs0YYUPAgEoUUk6wabV0kaFYscsJFUwndwSCQFZ7WEU8BEHQ4EP8QeYEUEGiBYKA-MNkpE8ckiFRdSOECQv1eYarWOF4HIrTBwvWL6VDCIIAA */
  id: "imageEditor",
  types: {
    context: {} as ImageEditorContext,
    events: {} as ImageEditorEvent,
  },
  context: ({ input }: { input: { configuration: Configuration; onSave: (canvases: Canvas[]) => Promise<void> } }) => ({
    canvasApis: [] as Canvas[],
    current: 0,
    circleCounter: 1,
    squareCounter: 1,
    selectedColor: "#008000",
    showNumbers: true,
    shapeSize: "l" as const,
    zoomLevel: 1,
    files: [],
    configuration: input.configuration,
    onSave: input.onSave,
    logo: undefined,
  }),
  initial: "idle",
  states: {
    idle: {
      entry: ["setupIdleMode"],
      on: {
        LOAD_FILES: {
          target: "loading",
          actions: assign({
            files: ({ event }) => event.files,
          }),
        },
        CANVASES_INITIALIZED: {
          actions: assign({
            canvasApis: ({ event }) => event.canvasApis,
          }),
        },
        TOGGLE_CIRCLE_MODE: {
          target: "circleMode",
          actions: "escapeOtherModes",
        },
        TOGGLE_SQUARE_MODE: {
          target: "squareMode", 
          actions: "escapeOtherModes",
        },
        TOGGLE_PAINT_MODE: {
          target: "paintMode",
          actions: "escapeOtherModes",
        },
        TOGGLE_PAN_MODE: {
          target: "panMode",
          actions: "escapeOtherModes",
        },
        TOGGLE_NUMBERS: {
          actions: assign({
            showNumbers: ({ context }) => !context.showNumbers,
          }),
        },
        SET_COLOR: {
          actions: assign({
            selectedColor: ({ event }) => event.color,
          }),
        },
        SET_SHAPE_SIZE: {
          actions: assign({
            shapeSize: ({ event }) => event.size,
          }),
        },
        SET_CIRCLE_COUNTER: {
          actions: assign({
            circleCounter: ({ event }) => event.counter,
          }),
        },
        SET_SQUARE_COUNTER: {
          actions: assign({
            squareCounter: ({ event }) => event.counter,
          }),
        },
        GO_TO_SLIDE: {
          actions: [
            "cleanupCanvasListeners",
            assign({
              current: ({ event }) => event.index,
            }),
            "updateZoomLevel",
            "setupCircleMode",
          ],
        },
        ZOOM_IN: {
          actions: "handleZoomIn",
        },
        ZOOM_OUT: {
          actions: "handleZoomOut",
        },
        RESET_ZOOM: {
          actions: "handleResetZoom",
        },
        REMOVE_IMAGE: {
          actions: "handleRemoveImage",
        },
        REMOVE_ALL_IMAGES: {
          actions: "handleRemoveAllImages",
        },
        EXPORT_PDF: {
          target: "exporting",
        },
        ESCAPE_ALL_MODES: {
          target: "idle",
        },
      },
    },
    loading: {
      invoke: {
        src: fromPromise(async ({ input }: { input: { files: File[]; configuration: Configuration } }) => {
          const { files, configuration } = input;

          const logo = await fetch(configuration?.logoUrl ?? "/typst-templates/layouts/BERNARDINO_logo.svg").then(r => r.arrayBuffer().then(b => new Uint8Array(b)));
          const logoFile = new File([logo], "logo", { type: "image/svg+xml" });

          const allFiles: Promise<File[]>[] = [];

          for (const file of files) {
            allFiles.push(file.type === "application/pdf" ? pdf2pngs(file) : Promise.resolve([file]));
          }

          const filepaths = (await Promise.all(allFiles)).flat();

          const pdfResult = await typstLayoutTemplate("/typst-templates/layouts", {
            page_size: configuration?.paperSize ?? "a4",
            margin: configuration?.marginIn ?? 0.5,
            logo: logoFile,
            date: new Date().toISOString().split("T")[0],
            year: new Date().getFullYear(),
            filepaths
          });

          if (pdfResult.err) {
            console.error(pdfResult.val);
            throw new Error(pdfResult.val.join(", "));
          }

          // Convert PDF to PNGs
          const pdfFile = pdfResult.val; 
          const pngFiles = await pdf2pngs(pdfFile);

          const newCanvasApis: Canvas[] = [];

          for (const [index, imageFile] of pngFiles.entries()) {
            const canvasHeight = 600;
            const [width, height] = configuration?.paperSize === "us-legal" ? PageSizes.Legal : configuration?.paperSize === "us-letter" ? PageSizes.Letter : PageSizes.A4;
            const ratio = width / height;
            const canvasWidth = canvasHeight * ratio;

            // Create HTML canvas element
            const htmlCanvas = document.createElement('canvas');
            htmlCanvas.id = `canvas-${index}`;
            htmlCanvas.width = canvasWidth;
            htmlCanvas.height = canvasHeight;

            // Create Fabric Canvas
            const canvas = new Canvas(htmlCanvas, {
              width: canvasWidth,
              height: canvasHeight,
            });

            // Create image URL from file
            const imageUrl = URL.createObjectURL(imageFile);

            // Create FabricImage from the image URL
            const fabricImage = await FabricImage.fromURL(imageUrl);
            
            fabricImage.set({
              left: 0,
              top: 0,
              selectable: false,
              evented: false,
            });

            // Scale the image to fit the canvas
            const scale = Math.min(canvasWidth / fabricImage.width!, canvasHeight / fabricImage.height!);
            fabricImage.scale(scale);

            // Center the image
            const scaledWidth = fabricImage.width! * scale;
            const scaledHeight = fabricImage.height! * scale;
            fabricImage.set({
              left: (canvasWidth - scaledWidth) / 2,
              top: (canvasHeight - scaledHeight) / 2,
            });

            canvas.add(fabricImage);
            canvas.renderAll();

            // Add mouse wheel zoom
            canvas.on('mouse:wheel', (opt) => {
              const delta = opt.e.deltaY;
              let zoom = canvas.getZoom();
              zoom *= 0.999 ** delta;
              if (zoom > 20) zoom = 20;
              if (zoom < 0.5) zoom = 0.5;
              
              const pointer = canvas.getPointer(opt.e);
              canvas.zoomToPoint(pointer, zoom);
              opt.e.preventDefault();
              opt.e.stopPropagation();
            });

            newCanvasApis.push(canvas);
          }

          return { canvasApis: newCanvasApis, logo: logoFile };
        }),
        input: ({ context }) => ({ 
          files: context.files, 
          configuration: context.configuration!
        }),
        onDone: {
          target: "idle",
          actions: assign({
            canvasApis: ({ context, event }) => [...context.canvasApis, ...event.output.canvasApis],
            logo: ({ event }) => event.output.logo
          }),
        },
        onError: {
          target: "idle",
          actions: [
            assign({
              error: ({ event }) => (event.error as Error)?.message || "Error loading files",
            }),
            ({ event }) => {
              console.error(event.error);
              const errorMessage = (event.error as Error)?.message || "Error loading files";
              toast.error("Error al procesar los archivos", {
                description: errorMessage,
              });
            }
          ],
        },
      },
    },
    circleMode: {
      entry: ["setupCircleMode"],
      exit: ["cleanupCanvasListeners"],
      on: {
        TOGGLE_CIRCLE_MODE: {
          target: "idle",
        },
        TOGGLE_SQUARE_MODE: {
          target: "squareMode",
          actions: "escapeOtherModes",
        },
        TOGGLE_PAINT_MODE: {
          target: "paintMode",
          actions: "escapeOtherModes",
        },
        TOGGLE_PAN_MODE: {
          target: "panMode",
          actions: "escapeOtherModes",
        },
        CANVAS_CLICK: {
          actions: "handleCircleClick",
        },
        ESCAPE_ALL_MODES: {
          target: "idle",
        },
        SET_COLOR: {
          actions: assign({
            selectedColor: ({ event }) => event.color,
          }),
        },
        SET_SHAPE_SIZE: {
          actions: assign({
            shapeSize: ({ event }) => event.size,
          }),
        },
        SET_CIRCLE_COUNTER: {
          actions: assign({
            circleCounter: ({ event }) => event.counter,
          }),
        },
        INCREMENT_CIRCLE_COUNTER: {
          actions: "incrementCircleCounter",
        },
        TOGGLE_NUMBERS: {
          actions: assign({
            showNumbers: ({ context }) => !context.showNumbers,
          }),
        },
        GO_TO_SLIDE: {
          actions: [
            "cleanupCanvasListeners",
            assign({
              current: ({ event }) => event.index,
            }),
            "updateZoomLevel",
            "setupCircleMode",
          ],
        },
        ZOOM_IN: {
          actions: "handleZoomIn",
        },
        ZOOM_OUT: {
          actions: "handleZoomOut",
        },
        RESET_ZOOM: {
          actions: "handleResetZoom",
        },
        REMOVE_IMAGE: {
          actions: "handleRemoveImage",
        },
        REMOVE_ALL_IMAGES: {
          actions: "handleRemoveAllImages",
        },
        EXPORT_PDF: {
          target: "exporting",
        },
      },
    },
    squareMode: {
      entry: ["setupSquareMode"],
      exit: ["cleanupCanvasListeners"],
      on: {
        TOGGLE_SQUARE_MODE: {
          target: "idle",
        },
        TOGGLE_CIRCLE_MODE: {
          target: "circleMode",
          actions: "escapeOtherModes",
        },
        TOGGLE_PAINT_MODE: {
          target: "paintMode",
          actions: "escapeOtherModes",
        },
        TOGGLE_PAN_MODE: {
          target: "panMode",
          actions: "escapeOtherModes",
        },
        CANVAS_CLICK: {
          actions: "handleSquareClick",
        },
        ESCAPE_ALL_MODES: {
          target: "idle",
        },
        SET_COLOR: {
          actions: assign({
            selectedColor: ({ event }) => event.color,
          }),
        },
        SET_SHAPE_SIZE: {
          actions: assign({
            shapeSize: ({ event }) => event.size,
          }),
        },
        SET_SQUARE_COUNTER: {
          actions: assign({
            squareCounter: ({ event }) => event.counter,
          }),
        },
        INCREMENT_SQUARE_COUNTER: {
          actions: "incrementSquareCounter",
        },
        TOGGLE_NUMBERS: {
          actions: assign({
            showNumbers: ({ context }) => !context.showNumbers,
          }),
        },
        GO_TO_SLIDE: {
          actions: [
            "cleanupCanvasListeners",
            assign({
              current: ({ event }) => event.index,
            }),
            "updateZoomLevel",
            "setupSquareMode",
          ],
        },
        ZOOM_IN: {
          actions: "handleZoomIn",
        },
        ZOOM_OUT: {
          actions: "handleZoomOut",
        },
        RESET_ZOOM: {
          actions: "handleResetZoom",
        },
        REMOVE_IMAGE: {
          actions: "handleRemoveImage",
        },
        REMOVE_ALL_IMAGES: {
          actions: "handleRemoveAllImages",
        },
        EXPORT_PDF: {
          target: "exporting",
        },
      },
    },
    paintMode: {
      entry: ["setupPaintMode"],
      exit: ["cleanupCanvasListeners"],
      on: {
        TOGGLE_PAINT_MODE: {
          target: "idle",
        },
        TOGGLE_CIRCLE_MODE: {
          target: "circleMode",
          actions: "escapeOtherModes",
        },
        TOGGLE_SQUARE_MODE: {
          target: "squareMode",
          actions: "escapeOtherModes",
        },
        TOGGLE_PAN_MODE: {
          target: "panMode",
          actions: "escapeOtherModes",
        },
        CANVAS_CLICK: {
          actions: "handlePaintClick",
        },
        ESCAPE_ALL_MODES: {
          target: "idle",
        },
        SET_COLOR: {
          actions: assign({
            selectedColor: ({ event }) => event.color,
          }),
        },
        GO_TO_SLIDE: {
          actions: [
            "cleanupCanvasListeners",
            assign({
              current: ({ event }) => event.index,
            }),
            "updateZoomLevel",
            "setupPaintMode",
          ],
        },
        ZOOM_IN: {
          actions: "handleZoomIn",
        },
        ZOOM_OUT: {
          actions: "handleZoomOut",
        },
        RESET_ZOOM: {
          actions: "handleResetZoom",
        },
        REMOVE_IMAGE: {
          actions: "handleRemoveImage",
        },
        REMOVE_ALL_IMAGES: {
          actions: "handleRemoveAllImages",
        },
        EXPORT_PDF: {
          target: "exporting",
        },
      },
    },
    panMode: {
      entry: ["setupPanMode"],
      exit: ["cleanupPanMode"],
      on: {
        TOGGLE_PAN_MODE: {
          target: "idle",
        },
        TOGGLE_CIRCLE_MODE: {
          target: "circleMode",
          actions: "escapeOtherModes",
        },
        TOGGLE_SQUARE_MODE: {
          target: "squareMode",
          actions: "escapeOtherModes",
        },
        TOGGLE_PAINT_MODE: {
          target: "paintMode",
          actions: "escapeOtherModes",
        },
        ESCAPE_ALL_MODES: {
          target: "idle",
        },
        GO_TO_SLIDE: {
          actions: [
            assign({
              current: ({ event }) => event.index,
            }),
            "updateZoomLevel",
          ],
        },
        ZOOM_IN: {
          actions: "handleZoomIn",
        },
        ZOOM_OUT: {
          actions: "handleZoomOut",
        },
        RESET_ZOOM: {
          actions: "handleResetZoom",
        },
        REMOVE_IMAGE: {
          actions: "handleRemoveImage",
        },
        REMOVE_ALL_IMAGES: {
          actions: "handleRemoveAllImages",
        },
        EXPORT_PDF: {
          target: "exporting",
        },
      },
    },
    exporting: {
      invoke: {
        src: fromPromise(async ({ input }: { input: { canvasApis: Canvas[]; onSave: (canvases: Canvas[]) => Promise<void> } }) => {
          const { canvasApis, onSave } = input;
          
          // Store current zoom levels and viewport transforms
          const originalStates = canvasApis.map((canvas) => ({
            zoom: canvas.getZoom(),
            viewport: canvas.viewportTransform.slice() as [number, number, number, number, number, number]
          }));

          try {
            // Reset all canvases to 100% zoom for export
            canvasApis.forEach((canvas) => {
              canvas.setZoom(1);
              canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
              canvas.renderAll();
            });

            // Export the PDF
            await onSave(canvasApis);

            return { originalStates };
          } finally {
            // Restore original zoom levels and viewport transforms
            canvasApis.forEach((canvas, index) => {
              const originalState = originalStates[index];
              if (originalState) {
                canvas.setZoom(originalState.zoom);
                canvas.setViewportTransform(originalState.viewport);
                canvas.renderAll();
              }
            });
          }
        }),
        input: ({ context }) => ({ 
          canvasApis: context.canvasApis, 
          onSave: context.onSave!
        }),
        onDone: {
          target: "idle",
          actions: "updateCurrentZoomLevel",
        },
        onError: {
          target: "idle",
          actions: [
            assign({
              error: ({ event }) => (event.error as Error)?.message || "Error exporting PDF",
            }),
            ({ event }) => {
              const errorMessage = (event.error as Error)?.message || "Error exporting PDF";
              toast.error("Error al exportar PDF", {
                description: errorMessage,
              });
            }
          ],
        },
      },
    },
  },
}, {
  actions: {
    setupIdleMode: ({ context }) => {
      const currentCanvas = context.canvasApis[context.current];
      if (currentCanvas) {
        currentCanvas.selection = true;
        currentCanvas.defaultCursor = 'default';
        currentCanvas.hoverCursor = 'move';
        currentCanvas.off('mouse:down');
        currentCanvas.off('mouse:move');
        currentCanvas.off('mouse:up');
      }
    },
    
    setupCircleMode: ({ context, self }) => {
      const currentCanvas = context.canvasApis[context.current];
      if (!currentCanvas) {
        console.error('No current canvas found!');
        return;
      }
      
      currentCanvas.defaultCursor = 'crosshair';
      currentCanvas.hoverCursor = 'crosshair';
      currentCanvas.selection = true;
      
      const handleCanvasClick = (event: { 
        e: TPointerEvent; 
        pointer?: { x: number; y: number }; 
        target?: FabricObject 
      }) => {
        const pointer = currentCanvas.getScenePoint(event.e);
        
        // Get current context from the state machine
        const currentContext = self.getSnapshot().context;
        const size = currentContext.shapeSize === "s" ? 8 : currentContext.shapeSize === "m" ? 12 : 18; 
        
        const circle = new Circle({
          left: pointer.x - size/2,
          top: pointer.y - size/2,
          radius: size/2,
          fill: hexToRgba(currentContext.selectedColor, 0.5),
          stroke: currentContext.selectedColor,
          strokeWidth: 1,
          selectable: true,
        });

        if (currentContext.showNumbers && typeof currentContext.circleCounter === 'number') {
          const fontSize = size/2;
          const text = new IText(currentContext.circleCounter.toString(), {
            left: pointer.x - fontSize/2,
            top: pointer.y - fontSize/2,
            width: size,
            fontSize,
            fill: 'rgba(0, 0, 0, 0.75)',
            textAlign: 'center',
            selectable: false,
            evented: false,
          });
          
          const group = new Group([circle, text], {
            left: pointer.x - size/2,
            top: pointer.y - size/2,
            selectable: true,
          });
          
          currentCanvas.add(group);
        } else {
          currentCanvas.add(circle);
        }
        
        currentCanvas.renderAll();

        // Send increment event
        if (currentContext.showNumbers && typeof currentContext.circleCounter === 'number') {
          self.send({ type: "INCREMENT_CIRCLE_COUNTER" });
        }
      };
      
      currentCanvas.on("mouse:down", handleCanvasClick);
    },
    
    setupSquareMode: ({ context, self }) => {
      const currentCanvas = context.canvasApis[context.current];
      
      if (!currentCanvas) {
        console.error('No current canvas found!');
        return;
      }
      
      currentCanvas.defaultCursor = 'crosshair';
      currentCanvas.hoverCursor = 'crosshair';
      currentCanvas.selection = true;
      
      const handleCanvasClick = (event: { 
        e: TPointerEvent; 
        pointer?: { x: number; y: number }; 
        target?: FabricObject 
      }) => {
        const pointer = currentCanvas.getScenePoint(event.e);
        
        // Get current context from the state machine
        const currentContext = self.getSnapshot().context;
        const size = currentContext.shapeSize === "s" ? 8 : currentContext.shapeSize === "m" ? 12 : 18;
        
        const rect = new Rect({
          left: pointer.x - size/2,
          top: pointer.y - size/2,
          width: size,
          height: size,
          fill: hexToRgba(currentContext.selectedColor, 0.5),
          stroke: currentContext.selectedColor,
          strokeWidth: 1,
          selectable: true,
        });

        if (currentContext.showNumbers && typeof currentContext.squareCounter === 'number') {
          const fontSize = size/2;
          const text = new IText(currentContext.squareCounter.toString(), {
            left: pointer.x - fontSize/2,
            top: pointer.y - fontSize/2,
            width: size,
            fontSize,
            fill: 'rgba(0, 0, 0, 0.75)',
            textAlign: 'center',
            selectable: false,
            evented: false,
          });
          
          const group = new Group([rect, text], {
            left: pointer.x - size/2,
            top: pointer.y - size/2,
            selectable: true,
          });
          
          currentCanvas.add(group);
        } else {
          currentCanvas.add(rect);
        }
        
        currentCanvas.renderAll();

        // Send increment event
        if (currentContext.showNumbers && typeof currentContext.squareCounter === 'number') {
          self.send({ type: "INCREMENT_SQUARE_COUNTER" });
        }
      };
      
      currentCanvas.on("mouse:down", handleCanvasClick);
    },
    
    setupPaintMode: ({ context, self }) => {
      const currentCanvas = context.canvasApis[context.current];
      if (currentCanvas) {
        currentCanvas.defaultCursor = 'crosshair';
        currentCanvas.hoverCursor = 'crosshair';
        currentCanvas.selection = true;
        
        const paintShape = (shape: FabricObject, currentContext: ImageEditorContext) => {
          if (shape.type === "group") {
            // Handle grouped objects (shapes with numbers)
            const objects = (shape as Group).getObjects();
            objects.forEach((obj: FabricObject) => {
              if (obj.type === "circle" || obj.type === "rect") {
                obj.set({
                  fill: hexToRgba(currentContext.selectedColor, 0.5),
                  stroke: currentContext.selectedColor,
                });
              }
            });
          } else if (shape.type === "circle" || shape.type === "rect") {
            // Handle individual shapes
            shape.set({
              fill: hexToRgba(currentContext.selectedColor, 0.5),
              stroke: currentContext.selectedColor,
            });
          }
        };
        
        const handleCanvasClick = (opt: { target?: FabricObject; e: TPointerEvent }) => {
          // Get current context from the state machine
          const currentContext = self.getSnapshot().context;
          
          // First check if there are multiple objects selected
          const activeObjects = currentCanvas.getActiveObjects();
          
          if (activeObjects && activeObjects.length > 1) {
            // Paint all selected objects
            activeObjects.forEach((obj: FabricObject) => {
              paintShape(obj, currentContext);
            });
            currentCanvas.renderAll();
          } else if (activeObjects && activeObjects.length === 1) {
            // Paint the single selected object
            paintShape(activeObjects[0], currentContext);
            currentCanvas.renderAll();
          } else {
            // Handle single object click when nothing is selected
            const clickedObject = opt.target;
            
            if (clickedObject) {
              paintShape(clickedObject, currentContext);
              currentCanvas.renderAll();
            }
          }
        };
        
        currentCanvas.on("mouse:down", handleCanvasClick);
      }
    },
    
    setupPanMode: ({ context }) => {
      const currentCanvas = context.canvasApis[context.current];
      if (currentCanvas) {
        currentCanvas.selection = false;
        currentCanvas.defaultCursor = 'grab';
        currentCanvas.hoverCursor = 'grab';
        
        let isDragging = false;
        let lastPosX = 0;
        let lastPosY = 0;
        
        const handleMouseDown = (opt: { e: TPointerEvent }) => {
          const evt = opt.e as MouseEvent;
          isDragging = true;
          currentCanvas.selection = false;
          lastPosX = evt.clientX;
          lastPosY = evt.clientY;
          currentCanvas.defaultCursor = 'grabbing';
        };

        const handleMouseMove = (opt: { e: TPointerEvent }) => {
          if (isDragging) {
            const e = opt.e as MouseEvent;
            const vpt = currentCanvas.viewportTransform;
            if (vpt) {
              vpt[4] += e.clientX - lastPosX;
              vpt[5] += e.clientY - lastPosY;
              currentCanvas.requestRenderAll();
              lastPosX = e.clientX;
              lastPosY = e.clientY;
            }
          }
        };

        const handleMouseUp = () => {
          currentCanvas.setViewportTransform(currentCanvas.viewportTransform);
          isDragging = false;
          currentCanvas.selection = false;
          currentCanvas.defaultCursor = 'grab';
        };

        currentCanvas.on('mouse:down', handleMouseDown);
        currentCanvas.on('mouse:move', handleMouseMove);
        currentCanvas.on('mouse:up', handleMouseUp);
      }
    },
    
    cleanupCanvasListeners: ({ context }) => {
      const currentCanvas = context.canvasApis[context.current];
      if (currentCanvas) {
        currentCanvas.off('mouse:down');
        currentCanvas.off('mouse:move');
        currentCanvas.off('mouse:up');
      }
    },
    
    cleanupPanMode: ({ context }) => {
      const currentCanvas = context.canvasApis[context.current];
      if (currentCanvas) {
        currentCanvas.selection = true;
        currentCanvas.defaultCursor = 'default';
        currentCanvas.hoverCursor = 'move';
        currentCanvas.off('mouse:down');
        currentCanvas.off('mouse:move');
        currentCanvas.off('mouse:up');
      }
    },
    
    escapeOtherModes: ({ context }) => {
      // Clean up any existing listeners on current canvas
      const currentCanvas = context.canvasApis[context.current];
      if (currentCanvas) {
        currentCanvas.off('mouse:down');
        currentCanvas.off('mouse:move');
        currentCanvas.off('mouse:up');
      }
    },
    updateZoomLevel: assign({
      zoomLevel: ({ context }) => context.canvasApis[context.current]?.getZoom() || 1,
    }),
    updateCurrentZoomLevel: assign({
      zoomLevel: ({ context }) => context.canvasApis[context.current]?.getZoom() || 1,
    }),
    handleZoomIn: assign({
      zoomLevel: ({ context }) => {
        const canvas = context.canvasApis[context.current];
        if (canvas) {
          const newZoom = Math.min(context.zoomLevel * 1.2, 20);
          const center = new Point(canvas.width! / 2, canvas.height! / 2);
          canvas.zoomToPoint(center, newZoom);
          return newZoom;
        }
        return context.zoomLevel;
      },
    }),
    handleZoomOut: assign({
      zoomLevel: ({ context }) => {
        const canvas = context.canvasApis[context.current];
        if (canvas) {
          const newZoom = Math.max(context.zoomLevel / 1.2, 0.5);
          const center = new Point(canvas.width! / 2, canvas.height! / 2);
          canvas.zoomToPoint(center, newZoom);
          return newZoom;
        }
        return context.zoomLevel;
      },
    }),
    handleResetZoom: assign({
      zoomLevel: ({ context }) => {
        const canvas = context.canvasApis[context.current];
        if (canvas) {
          canvas.setZoom(1);
          canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
          return 1;
        }
        return context.zoomLevel;
      },
    }),
    handleCircleClick: ({ context, event }) => {
      if (event.type === "CANVAS_CLICK") {
        const canvasClickEvent = event as Extract<ImageEditorEvent, { type: "CANVAS_CLICK" }>;
        const { canvas, pointer } = canvasClickEvent;
        const r = context.shapeSize === "l" ? 8 : context.shapeSize === "m" ? 6 : 4;
        
        const hexToRgba = (hex: string, opacity: number = 1) => {
          const r = parseInt(hex.slice(1, 3), 16);
          const g = parseInt(hex.slice(3, 5), 16);
          const b = parseInt(hex.slice(5, 7), 16);
          return `rgba(${r}, ${g}, ${b}, ${opacity})`;
        };

        const circle = new Circle({
          radius: r,
          fill: hexToRgba(context.selectedColor, 0.3),
          stroke: context.selectedColor,
          strokeWidth: 0.5,
          selectable: false,
          evented: false,
          originX: "center",
          originY: "center",
        });

        const elements: FabricObject[] = [circle];

        if (context.showNumbers) {
          const text = new IText(context.circleCounter.toString(), {
            fontSize: r,
            fontFamily: "Arial",
            fill: "#000000",
            textAlign: "center",
            originX: "center",
            originY: "center",
            selectable: false,
            evented: false,
          });
          elements.push(text);
        }

        const group = new Group(elements, {
          left: pointer.x - r,
          top: pointer.y - r,
          selectable: true,
          evented: true,
          cornerSize: 4,
          hasControls: true,
          hasBorders: true,
        });

        canvas.add(group);
        canvas.setActiveObject(group);
        canvas.renderAll();
      }
    },
    handleSquareClick: ({ context, event }) => {
      if (event.type === "CANVAS_CLICK") {
        const canvasClickEvent = event as Extract<ImageEditorEvent, { type: "CANVAS_CLICK" }>;
        const { canvas, pointer } = canvasClickEvent;
        const size = context.shapeSize === "l" ? 16 : context.shapeSize === "m" ? 12 : 8;
        
        const hexToRgba = (hex: string, opacity: number = 1) => {
          const r = parseInt(hex.slice(1, 3), 16);
          const g = parseInt(hex.slice(3, 5), 16);
          const b = parseInt(hex.slice(5, 7), 16);
          return `rgba(${r}, ${g}, ${b}, ${opacity})`;
        };

        const square = new Rect({
          width: size,
          height: size,
          fill: hexToRgba(context.selectedColor, 0.3),
          stroke: context.selectedColor,
          strokeWidth: 0.5,
          selectable: false,
          evented: false,
          originX: "center",
          originY: "center",
        });

        const elements: FabricObject[] = [square];

        if (context.showNumbers) {
          const text = new IText(context.squareCounter.toString(), {
            left: pointer.x - size/4,
            top: pointer.y - size/4,
            fontSize: Math.max(8, size/3),
            fill: 'rgba(0, 0, 0, 0.75)',
            textAlign: 'center',
            originX: 'center',
            originY: 'center',
            selectable: false,
            evented: false,
          });
          elements.push(text);
        }

        const group = new Group(elements, {
          left: pointer.x - size / 2,
          top: pointer.y - size / 2,
          selectable: true,
          evented: true,
          cornerSize: 4,
          hasControls: true,
          hasBorders: true,
        });

        canvas.add(group);
        canvas.setActiveObject(group);
        canvas.renderAll();
      }
    },
    handlePaintClick: ({ context, event }) => {
      if (event.type === "CANVAS_CLICK") {
        const canvasClickEvent = event as Extract<ImageEditorEvent, { type: "CANVAS_CLICK" }>;
        const { canvas, target } = canvasClickEvent;
        
        if (target) {
          const hexToRgba = (hex: string, opacity: number = 1) => {
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${opacity})`;
          };

          if (target.type === "group") {
            const objects = (target as Group).getObjects();
            objects.forEach((obj: FabricObject) => {
              if (obj.type === "circle" || obj.type === "rect") {
                obj.set({
                  fill: hexToRgba(context.selectedColor, 0.3),
                  stroke: context.selectedColor,
                });
              }
            });
          } else if (target.type === "circle" || target.type === "rect") {
            target.set({
              fill: hexToRgba(context.selectedColor, 0.3),
              stroke: context.selectedColor,
            });
          }
          canvas.renderAll();
        }
      }
    },
    handleRemoveImage: assign({
      canvasApis: ({ context, event }) => {
        if (event.type === "REMOVE_IMAGE") {
          const removeEvent = event as Extract<ImageEditorEvent, { type: "REMOVE_IMAGE" }>;
          // Dispose of the canvas to free memory
          if (context.canvasApis[removeEvent.index]) {
            //context.canvasApis[removeEvent.index]?.elements.dispose();
          }
          return context.canvasApis.filter((_, i) => i !== removeEvent.index);
        }
        return context.canvasApis;
      },
      current: ({ context, event }) => {
        if (event.type === "REMOVE_IMAGE") {
          return Math.max(0, Math.min(context.current, context.canvasApis.length - 2));
        }
        return context.current;
      },
    }),
    handleRemoveAllImages: assign({
      canvasApis: ({ context }) => {
        // Dispose of all canvases
        context.canvasApis.forEach((canvas) => canvas.dispose());
        return [];
      },
      circleCounter: () => 1,
      squareCounter: () => 1,
      current: () => 0,
      zoomLevel: () => 1,
    }),
    incrementCircleCounter: assign({
      circleCounter: ({ context }) => 
        context.showNumbers && typeof context.circleCounter === "number" 
          ? context.circleCounter + 1 
          : context.circleCounter,
    }),
    incrementSquareCounter: assign({
      squareCounter: ({ context }) => 
        context.showNumbers && typeof context.squareCounter === "number" 
          ? context.squareCounter + 1 
          : context.squareCounter,
    }),
  },
}); 